
import { Component, Vue, Watch, Prop, InjectReactive } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { AdvancedSearchFilter, CalculationDataTypeValue, CalculationType, Case, dataTypeByFieldType, Field } from "@/types"
import { Insight, InsightCalculation, LabelFormat, insightViews, Operator, operatorsByFieldType, operatorLabels, DynamicParameterValue, DynamicParameter, DataType, GroupByDateFormat, InsightSummary } from "@/types/insights"
import { caseColumns, CaseColumn } from "@/types/caseColumns"
import SkeletonLoader from "../SkeletonLoader.vue"
import vSelect from "vue-select"

import api from "@/services/api"

import ChartResults from "@/components/Insights/ChartResults.vue"
import ChartOptions from "@/components/Insights/ChartOptions.vue"
import TableResults from "@/components/Insights/TableResults.vue"
import HeatMap from "@/components/Insights/HeatMap.vue"
import CalculationTable from "@/components/Insights/CalculationTable.vue"
import { Clause, DayOfWeek, defaultSearchClauseParameters, SearchClauseParameters } from "@/types/view"
import { DashboardState } from "@/types/dashboard"
import { defaultFieldOperator } from "@/types/insights/operators"
import Guid from "@/scripts/guid"

import ParameterValue from "@/components/Controls/ParameterValue.vue"
import DynamicParameterValueControl from "@/components/Insights/DynamicParameterValue.vue"

import { filterMixin } from "@/mixins/filtering"
import { CalculationResults, FilteredCalculationResults } from "@/types/insights/calculatedResults"
import positionDropdown from "@/scripts/VSelectDropdownPosition.ts"
import { CurrentPageDetails } from "@/types/paginatedRequest"

const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"skeleton": SkeletonLoader,
		"chart-results": ChartResults,
		"results-table": TableResults,
		"calculation-table": CalculationTable,
		"heat-map": HeatMap,
		"v-select": vSelect,
		"chart-options": ChartOptions,
		"parameter-value": ParameterValue,
		"dynamic-parameter-value": DynamicParameterValueControl
	}
})
export default class Chart extends Vue {
	@Prop()
	value: any;

	@Prop()
	edit!: boolean

	@Prop({ default: () => { return { calculationTableClickedRows: {} } } })
	dashboardState!: DashboardState;

	@setup.Action loadFieldsIfUnloaded!: () => Promise<Field[]>
	@setup.State fields!: Field[];

	@setup.Getter("getAllFields")
	public allCaseFields!: CaseColumn[]

	loading: boolean = true;

	/** Used to ensure that results of an older request don't overwrite a newer one if received out of order **/
	lastRequestStartedAt: Date | null = null;

	displayRecords: Case[] = [];

	insights: InsightSummary[] = [];
	insightParameters: DynamicParameter[] = []

	selectedInsightId: string | null = null;
	selectedInsight: Insight | null = null;
	private selectedClauses: Clause[] | null = null;
	private selectedSorting = {
		sortBy: "",
		sortDesc: false,
		sortDataType: DataType.none
	};

	filterFromInsightId: string | null = null;
	filterFromInsight: Insight | null = null;
	filterFromGroupLabel: string | null = null;
	filterFromFieldName: string | null = null;
	filterFromLabelFormat: LabelFormat = LabelFormat.Text;

	insightFilterIsActive: boolean = false;
	filteredResultsLoaded: boolean = false
	filteredResultsLoading: boolean = false

	chartType: any = null;
	graphCalculations: string[] = []

	dynamicFilter: string | null = null
	dynamicFilterOperator: Operator | null = null
	dynamicFilterValue: any = null

	dynamicParameterValues: { [key: string]: DynamicParameterValue } = {}

	graphSingleCalculation: string | null = null
	errorLoading: boolean = false

	resultGroups: readonly CalculationResults[] | null = null
	reloadResultsKey: string | null = null;
	hasBeenRun: boolean = false
	private resultsUpdateHook: number = 0

	tableColumns: any[] = [];
	hideFilters: boolean = false

	labelFormat: LabelFormat = LabelFormat.Text;

	runInsightTimeout: NodeJS.Timeout | null = null

	private perPage: number = 20;
	private calculatedResultsId: string | null = null;

	chartOptions: any = {
		backgroundColor: "transparent",
		legend: {
			position: "top"
		},
		chartArea: {
			width: "90%",
			height: "80%"
		}
	}

	private calcDropdownPosition = positionDropdown;

	private get insightDisplayOptions () {
		if (!this.selectedInsight) return []
		return insightViews.filter(display => display.canDisplay(this.selectedInsight, this.selectedInsight!.calculations, this.fields, true))
	}

	private get calculations () {
		if (!this.selectedInsight) return []
		return this.selectedInsight.calculations
	}

	private get pieCalculations () {
		if (!this.selectedInsight) return []
		return this.selectedInsight.calculations.filter(c => !c.groupBy)
	}

	private get filterFromInsightOptions () : InsightSummary[] {
		return this.insights ? this.insights : []
	}

	private get showFilterFromUnselectedMessage () : boolean {
		return !!this.filterFromInsightId && !this.insightFilterIsActive
	}

	private get columnsByKey (): { [key: string]: CaseColumn } {
		return this.allCaseFields
			? this.allCaseFields.toDictionary("key")
			: {}
	}

	private get insightColumns (): CaseColumn[] {
		return this.selectedInsight
			? this.selectedInsight.columns.map(c => this.columnsByKey[c]).filter(c => !!c).sortBy("label")
			: []
	}

	private get dynamicFilterColumn (): CaseColumn | null {
		return this.dynamicFilter
			? this.columnsByKey[this.dynamicFilter] || null
			: null
	}

	private get dynamicFilterOperatorLabels (): { operator: Operator, label: string }[] {
		return operatorLabels.filter(o => this.dynamicFilterOperators.includes(o.operator))
	}

	private get dynamicFilterOperators (): Operator[] {
		return this.dynamicFilter && this.columnsByKey[this.dynamicFilter]
			? this.columnsByKey[this.dynamicFilter].operators
				? this.columnsByKey[this.dynamicFilter].operators
				: operatorsByFieldType[this.columnsByKey[this.dynamicFilter].dataType] || []
			: []
	}

	private get dynamicFilterClauses (): AdvancedSearchFilter[] {
		return !!this.dynamicFilterColumn && this.dynamicFilterOperator !== null && this.dynamicFilterValue !== null
			? [{
				key: this.dynamicFilter!,
				operator: this.dynamicFilterOperator,
				value: this.dynamicFilterValue,
				column: this.dynamicFilterColumn
			}]
			: []
	}

	private get dynamicFilterOperatorLabel (): string {
		return this.dynamicFilterOperator !== null
			? operatorLabels.findAndGet(l => l.operator === this.dynamicFilterOperator, l => l.label, "")
			: ""
	}

	private get filteredResultGroups () : FilteredCalculationResults[] {
		if (!this.selectedInsight || !this.resultGroups) return []

		const filterFunctions = filterMixin.methods.filtersToFunctions(this.dynamicFilterClauses, "")
		return filterMixin.methods.filterGroups((this.resultGroups as FilteredCalculationResults[]), filterFunctions, this.labelFormat, this.selectedInsight!, this.fields)
	}

	private get dynamicParameterValueList (): DynamicParameterValue[] {
		return Object.keys(this.dynamicParameterValues).map(key => this.dynamicParameterValues[key])
	}

	private get missingDynamicParameters (): boolean {
		return (!!this.availableDynamicParameters &&
			this.availableDynamicParameters.length > 0 &&
			this.availableDynamicParameters.some(p => !this.parameterIsValid(p)))
	}

	public async mounted () {
		if (this.edit) {
			this.insights = await api.insightsGet()

			if (!this.value || this.value.insightId === undefined) {
				this.$emit("input", {
					insightId: null,
					chartType: null
				})
			}
		}

		if (this.value && this.value.insightId) {
			this.selectedInsightId = this.value.insightId
			this.selectedInsight = await api.insightGet(this.value.insightId, true)

			if (this.selectedInsight && this.value.chartType) {
				const chartType = insightViews.find(c => c.key === this.value.chartType)
				if (chartType && chartType.key === "HeatMap") {
					await this.loadFieldsIfUnloaded()
				}

				if (chartType && chartType.canDisplay(this.selectedInsight, this.selectedInsight.calculations, this.fields, true)) {
					this.chartType = chartType
				}

				if (this.selectedInsight.includeGlobalParameters) {
					this.insightParameters = await api.insightParameters()
				}
			}

			this.graphCalculations = this.value.graphCalculations
				? this.value.graphCalculations
				: []
			this.graphSingleCalculation = this.value.graphSingleCalculation
				? this.value.graphSingleCalculation
				: null
		}

		if (this.value && this.value.filterFromInsightId) {
			this.filterFromInsightId = this.value.filterFromInsightId
			this.filterFromInsight = await api.insightGet(this.filterFromInsightId!, true)
			this.resultGroups = []
		}
		if (!this.edit && this.value && this.value.insightId) {
			this.onEditChanged()
		} else {
			this.loading = false
		}

		this.reloadResultsKey = Guid.newGuid()
	}

	@Watch("edit")
	async onEditChanged () {
		if (this.runInsightTimeout) {
			return
		}

		this.loading = true

		if (this.value) {
			this.dynamicFilter = this.value.dynamicFilter || null
			this.dynamicFilterOperator = this.value.dynamicFilterOperator === undefined ? null : this.value.dynamicFilterOperator
			this.hideFilters = this.value.hideFilters || false
		}

		if (this.edit) {
			await Promise.all([
				api.insightsGet().then(insights => this.insights = insights),
				this.loadFieldsIfUnloaded()
			])

			if (!this.value || this.value.insightId === undefined) {
				this.$emit("input", {
					insightId: null,
					chartType: null
				})
			}
		} else if (!this.edit && this.selectedInsight && this.chartType && !this.filterFromInsightId) {
			if (this.selectedInsight!.dynamicParameters) {
				this.selectedInsight!.dynamicParameters.forEach(p => {
					if (p.defaultValue) {
						Vue.set(this.dynamicParameterValues, p.key, {
							key: p.key,
							dataType: p.dataType,
							value: p.defaultValue,
							dynamicValue: p.dynamicValue || false,
							searchClauseParameters: p.searchClauseParameters
						})
					}
				})
			}

			await this.runInsight()
		}

		this.loading = false
	}

	public async runInsight (additionalClauses: Clause[] | null = null, page: number = 1) {
		if ((this.selectedInsight && this.chartType) && (
			!this.activeDynamicParameters ||
			this.activeDynamicParameters.length === 0 ||
			this.activeDynamicParameters.every(p => this.parameterIsValid(p))
		)) {
			this.hasBeenRun = true

			this.loading = true
			const runStartedAt = new Date(Date.now())
			this.lastRequestStartedAt = runStartedAt

			const insightData = await api.insightById(this.selectedInsight.id!, additionalClauses === null,
				(this.chartType.key === "table" || this.chartType.key === "HeatMap" || !!this.dynamicFilter),
				this.dynamicParameterValueList,
				additionalClauses,
				page,
				this.perPage,
				this.selectedSorting.sortBy,
				this.selectedSorting.sortDesc,
				this.selectedSorting.sortDataType
			)

			if (!this.lastRequestStartedAt || runStartedAt < this.lastRequestStartedAt) {
				return // another request was started after this one, so ignore the results
			}

			this.selectedClauses = additionalClauses ? [...additionalClauses] : null

			if (!insightData) {
				return
			}

			this.calculatedResultsId = insightData.id
			this.labelFormat = insightData.labelFormat

			if (this.chartType.key === "table" || this.chartType.key === "HeatMap") {
				const fields = await this.loadFieldsIfUnloaded()
				const allColumns = [
					...caseColumns,
					...fields.map(f => {
						return {
							label: f.title,
							key: "fields." + f.id,
							fieldId: f.id,
							dataType: f.dataType,
							fieldDataType: f.dataType === "calculated"
								? CalculationDataTypeValue[f.extensions!.calculationType as CalculationType]
								: dataTypeByFieldType[f.dataType!]
									? dataTypeByFieldType[f.dataType!]
									: null,
							dataSourceId: f.dataSourceId,
							dynamicField: true,
							fieldExtensions: f.extensions
						}
					})
				].toDictionary("key")

				this.tableColumns = this.selectedInsight!.columns.map(tableColumn => allColumns[tableColumn])
					.filter(c => !!c)
			}

			this.selectedInsight!.calculations.forEach(calculation => {
				Vue.set(calculation, "groupByKeyLabels", insightData.calculationGroupByLabels[calculation.key])
			})

			this.resultGroups = Object.freeze(insightData.results)

			if (additionalClauses) {
				this.filteredResultsLoaded = true
			}
		} else {
			this.resultGroups = null
		}

		this.loading = false
	}

	public parameterIsValid (p: DynamicParameter): boolean {
		if (!this.dynamicParameterValues[p.key] || this.dynamicParameterValues[p.key].value === null) {
			return false
		}

		switch (p.dataType) {
			case DataType.string:
				if (this.dynamicParameterValues[p.key].value === "") {
					// Treat empty strings as invalid to avoid inconsistent parameter input field behavior between data types e.g. numeric fields disallowing no value set
					return false
				}
				break
			case DataType.dateRange:
				const value = this.dynamicParameterValues[p.key].value
				if (value === "DateRange") {
					if (
						!this.dynamicParameterValues[p.key].searchClauseParameters ||
						!this.dynamicParameterValues[p.key].searchClauseParameters!.startDate ||
						!this.dynamicParameterValues[p.key].searchClauseParameters!.endDate
					) {
						return false
					}
				} else if (value === "MonthRange") {
					if (
						!this.dynamicParameterValues[p.key].searchClauseParameters ||
						!this.dynamicParameterValues[p.key].searchClauseParameters!.startMonthRange ||
						!this.dynamicParameterValues[p.key].searchClauseParameters!.endMonthRange
					) {
						return false
					}
				} else if (value === "WeekNumber") {
					if (
						!this.dynamicParameterValues[p.key].searchClauseParameters ||
						!this.dynamicParameterValues[p.key].searchClauseParameters!.weekNumber
					) {
						return false
					}
				}
				break
		}

		return true
	}

	public async selectInsight (insightId: string) {
		this.selectedInsightId = insightId
		this.selectedInsight = await api.insightGet(insightId, true)

		Vue.set(this.value, "insightId", insightId)
		this.$emit("input", this.value)
	}

	public async chooseFilterFromInsight (insightId: string | null) {
		if (!this.value.insightId) { // don't filter from insight if no primary selected
			return
		}

		if (insightId) {
			this.filterFromInsightId = insightId
			this.filterFromInsight = await api.insightGet(insightId, true)
		} else {
			this.filterFromInsightId = null
			this.filterFromInsight = null
		}
		Vue.set(this.value, "filterFromInsightId", insightId)
		this.$emit("input", this.value)
		this.resultGroups = []
	}

	public async selectChartType (chartType: any) {
		Vue.set(this.value, "chartType", chartType.key)
		this.$emit("input", this.value)
	}

	public async chartOptionsChanged (graphOptions: any) {
		Vue.set(this.value, "graphOptions", graphOptions)
		this.$emit("input", this.value)
	}

	public async graphSeriesChanged (graphCalculations: string[]) {
		Vue.set(this.value, "graphCalculations", graphCalculations)
		this.$emit("input", this.value)
	}

	public async graphSingleCalculationChanged (graphSingleCalculation: string) {
		Vue.set(this.value, "graphSingleCalculation", graphSingleCalculation)
		this.$emit("input", this.value)
	}

	@Watch("dashboardState.calculationTableClickedRows", { deep: true })
	public async filterResultGroups () {
		if (!this.filterFromInsightId || !this.selectedInsightId) {
			return
		}

		if (this.dashboardState.lastChangedFilterFromInsightId !== this.filterFromInsightId) {
			return
		}

		this.filteredResultsLoaded = false

		const clickedRow = this.dashboardState.calculationTableClickedRows[this.filterFromInsightId]

		// If we allow users to select another insight to filter from (filterFromInsightId != null),
		// do not load initial results (filterFromInsight has not been clicked)

		if (!clickedRow || clickedRow.label === "Total") {
			this.insightFilterIsActive = false
			this.resultGroups = []
			return
		}

		this.filteredResultsLoading = true
		const additionalClause: Clause = await this.convertRowGroupToClause(clickedRow)

		this.insightFilterIsActive = true
		this.filterFromGroupLabel = clickedRow.label === "" ? "None" : clickedRow.label
		this.reloadResultsKey = Guid.newGuid()

		await this.runInsight([additionalClause])
		this.filteredResultsLoading = false
	}

	private calculationTableRowClick (clickedRow: any) {
		this.$emit("calculationTableRowClick", this.selectedInsightId, clickedRow)
	}

	private async convertRowGroupToClause (selectedGroupRow: any) : Promise<Clause> {
		await this.loadFieldsIfUnloaded()
		const groupFieldId = this.filterFromInsight!.groupBy!

		const filterByCaseColumn = this.allCaseFields.find(c => c.key === groupFieldId)!

		this.filterFromFieldName = filterByCaseColumn.label

		let clauseOperator

		if (!selectedGroupRow.value) {
			clauseOperator = Operator.NotSet
		} else if (this.shouldFilterByDateRange()) {
			clauseOperator = Operator.DateRange
		} else if (this.shouldFilterByTeam()) {
			clauseOperator = Operator.InTeam
		} else {
			const equalityOperator = defaultFieldOperator[filterByCaseColumn.fieldDataType || DataType.none]
			clauseOperator = equalityOperator
		}

		this.setDateLabelFormat()

		const searchClauseParameters = this.getDateSearchParameters(selectedGroupRow)

		const clause : Clause = {
			dynamicField: filterByCaseColumn.dynamicField ? filterByCaseColumn.dynamicField : false,
			dynamicValue: this.shouldFilterByDateRange(),
			fieldDataType: filterByCaseColumn.fieldDataType!,
			label: "",
			value: this.shouldFilterByDateRange() ? "DateRange" : selectedGroupRow.value,
			searchClauseParameters: searchClauseParameters,
			field: groupFieldId,
			dataSourceId: filterByCaseColumn.dataSourceId || undefined,
			operator: clauseOperator,
			valueLabel: filterByCaseColumn.valueLabel,
			definitionId: filterByCaseColumn.definitionId || undefined,
			valueObject: filterByCaseColumn.valueObject
		}

		return clause
	}

	private setDateLabelFormat () {
		if (!this.shouldFilterByDateRange()) {
			this.filterFromLabelFormat = LabelFormat.Text
			return
		}

		this.filterFromLabelFormat = LabelFormat.Date
		if (this.filterFromInsight!.groupByDateFormat === GroupByDateFormat.Day) {
			this.filterFromLabelFormat = LabelFormat.Date
		} else if (this.filterFromInsight!.groupByDateFormat === GroupByDateFormat.Week) {
			this.filterFromLabelFormat = LabelFormat.WeekDate
		} else if (this.filterFromInsight!.groupByDateFormat === GroupByDateFormat.Month) {
			this.filterFromLabelFormat = LabelFormat.MonthDate
		}
	}

	private shouldFilterByDateRange () : boolean {
		return this.filterFromInsight!.groupByDataType === DataType.dateTime &&
									this.filterFromInsight!.groupByDateFormat !== undefined &&
									this.filterFromInsight!.groupByDateFormat !== null
	}

	private getDateSearchParameters (selectedGroupRow: any) : SearchClauseParameters {
		if (!this.shouldFilterByDateRange()) {
			return defaultSearchClauseParameters
		}

		let startDate : Date

		if (this.filterFromInsight!.groupByDateFormat === GroupByDateFormat.Quarter) {
			// Only seem to have .NET ticks for Quarters - label isn't a JS date
			// - so have to convert from .NET ticks to JS date
			const jsTicks = (parseInt(selectedGroupRow.value) - 621355968000000000) / 10000
			startDate = new Date(jsTicks)
		} else {
			startDate = new Date(selectedGroupRow.label)
		}

		let endDate = new Date(startDate)

		if (this.filterFromInsight!.groupByDateFormat === GroupByDateFormat.Day) {
			endDate.setDate(endDate.getDate() + 1)
		} else if (this.filterFromInsight!.groupByDateFormat === GroupByDateFormat.Week) {
			endDate.setDate(endDate.getDate() + 7)
		} else if (this.filterFromInsight!.groupByDateFormat === GroupByDateFormat.Month) {
			endDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1)
			endDate.setMonth(endDate.getMonth() + 1)
		} else if (this.filterFromInsight!.groupByDateFormat === GroupByDateFormat.Quarter) {
			endDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1)
			endDate.setMonth(endDate.getMonth() + 3)
		}

		endDate.setMilliseconds(endDate.getMilliseconds() - 1) // DataService uses >= and <= so need to make it just before

		// Dates should be converted to UTC automatically
		const searchClauseParameters = {
			startDayOfWeek: DayOfWeek.Monday,
			startDate: startDate,
			endDate: endDate,
			timeSpentClause: {
				searchValue: "",
				searchLabel: "",
				hoursSpent: 0,
				trackingFieldId: ""
			}
		}

		return searchClauseParameters
	}

	private shouldFilterByTeam () : boolean {
		return this.filterFromInsight!.groupByDataType === DataType.userRole && this.filterFromInsight!.groupByOption === "team"
	}

	private async clearCalculationTableFilter () {
		this.filterFromGroupLabel = null
		this.insightFilterIsActive = false
		this.resultGroups = []
	}

	public async updateValue (key: string, value: any) {
		Vue.set(this.value, key, value)
		this.$emit("input", this.value)
	}

	private get availableDynamicParameters () {
		if (!this.selectedInsight) return []

		var dynamicParameters: DynamicParameter[] = []

		if (this.selectedInsight!.dynamicParameters) {
			dynamicParameters = this.selectedInsight!.dynamicParameters.filter(p => !!p.key)
		}

		if (this.selectedInsight!.includeGlobalParameters) {
			// Add the global parameters to the dashboard parameters
			this.$emit("addGlobalParameters", this.selectedInsight!.includeGlobalParameters)

			this.selectedInsight!.includeGlobalParameters.forEach((parameterId: string) => {
				const parameters = this.insightParameters.filter(parameter => parameter.key === parameterId)
				dynamicParameters = dynamicParameters.concat(parameters)
			})
		}

		return dynamicParameters
	}

	/**
	 * The Dynamic Parameters that are in use i.e. referenced in the Insight's clauses / subclauses
	 */
	private get activeDynamicParameters () {
		if (!this.selectedInsight) return []

		return this.availableDynamicParameters.filter(p => this.selectedInsight!.clauses.some(c =>
			c.dynamicParameter === p.key ||
			(
				// is an OR subclause parameter
				!!c.orClause && !!c.subClauses &&
				c.subClauses.some(sc => sc.dynamicParameter === p.key)
			)
		))
	}

	public dynamicFilterChanged (value: string) {
		Vue.set(this.value, "dynamicFilterOperator", value)
		this.updateValue("dynamicFilter", value)
	}

	private setParameter (key: string, value: DynamicParameterValue | null) {
		Vue.set(this.dynamicParameterValues, key, value)

		if (!value?.isAltered) {
			return
		}

		if (!this.edit) {
			// Debounce running of insight to allow for all value changes to be applied
			if (this.runInsightTimeout) {
				clearTimeout(this.runInsightTimeout)
				this.runInsightTimeout = null
			}

			this.runInsightTimeout = setTimeout(() => {
				this.runInsight()
				this.runInsightTimeout = null
			}, 1000)
		}
	}

	private async loadNextPage (tableId: string, details: CurrentPageDetails): Promise<void> {
		if (!!this.selectedInsight && !!this.calculatedResultsId && !!details && !!this.resultGroups) {
			this.loading = true
			try {
				// We need to pass in the same data as though we're calling InsightById so we can get the Insight data if the data is not cached.
				const tableData: CalculationResults = await api.insightCasesForTable(this.selectedInsight.id!, this.selectedClauses === null,
					(this.chartType.key === "table" || this.chartType.key === "HeatMap" || !!this.dynamicFilter),
					this.dynamicParameterValueList,
					this.selectedClauses,
					details.pageNumber ?? 1,
					this.perPage,
					this.calculatedResultsId,
					tableId,
					details.sortBy ?? "",
					details.sortDesc ?? false,
					details.sortDataType ?? DataType.none
				)
				const toUpdate = (this.resultGroups as any[]).firstOrDefault(r => r.value === tableId)

				if (!!toUpdate && !!tableData && !!tableData.results) {
					Vue.set(toUpdate, "results", [...tableData.results])
					this.resultsUpdateHook++
				}
			} catch (err) {
				console.error(err)
			} finally {
				this.loading = false
			}
		} else {
			this.runInsight()
		}
	}

	private async reloadInsightBySort (details: CurrentPageDetails): Promise<void> {
		if (!!this.selectedInsight && !!this.calculatedResultsId && !!details && !!this.resultGroups) {
			try {
				this.loading = true
				const groupedResults = await api.insightCasesForTable(this.selectedInsight.id!, this.selectedClauses === null,
					(this.chartType.key === "table" || this.chartType.key === "HeatMap" || !!this.dynamicFilter),
					this.dynamicParameterValueList,
					this.selectedClauses, details.pageNumber ?? 1,
					this.perPage,
					this.calculatedResultsId,
					details.tableId ?? details.value,
					details.sortBy ?? "",
					details.sortDesc ?? false,
					details.sortDataType ?? DataType.none
				)
				const toUpdate = (this.resultGroups as any[]).firstOrDefault(r => r.value === details.value)

				if (toUpdate) {
					Vue.set(toUpdate, "results", [...groupedResults.results])
					this.resultsUpdateHook++
				}
			} catch (err) {
				console.error(err)
				this.runInsight(this.selectedClauses, this.selectedInsight?.page)
			} finally {
				this.loading = false
			}
		}
	}

	private async reloadInsightByClauses (clauses: Clause[]): Promise<void> {
		if (clauses) {
			this.selectedClauses = [...clauses]
			this.runInsight(this.selectedClauses)
		}
	}
}

